import { selector } from "recoil";
import {
  IUserSuggestionsForSearchCacheState,
  UserSuggestionsForSearchState,
} from "./userSuggestionsForSearch";
import { AllstarUser } from "./index";
import { ISearchItem } from "MVVM/Components/SearchBar";
import { randomAvatar } from "MVVM/Utilities/Images";

interface GraphQLUser {
  _id: string;
  username?: string;
  avatar?: string;
  userGroup?: {
    type: string;
  };
  rank?: number;
  reason?: string;
  steam?: {
    steamid?: string;
    personaname?: string;
  };
}

const toSearchItem = (user: GraphQLUser): ISearchItem => ({
  id: user._id,
  name: user.username!,
  image: user.avatar || randomAvatar(),
  userGroup: user.userGroup,
  rank: user.rank,
  reason: user.reason,
  steam: user.steam,
});

const filterAndMapUsers = (
  users: GraphQLUser[],
  maxCount: number = 20,
): ISearchItem[] => {
  return users
    .filter((user) => user._id && user.username)
    .slice(0, maxCount)
    .map(toSearchItem);
};

const getUserSuggestionsForSearch = (
  userSuggestionsForSearchCacheState: IUserSuggestionsForSearchCacheState,
  isLoggedIn: boolean,
): GraphQLUser[] => {
  const allSuggestedUsers: GraphQLUser[] = [];
  const loginStatePrefix = isLoggedIn ? "logged-in" : "logged-out";

  Object.entries(userSuggestionsForSearchCacheState).forEach(
    ([cacheKey, cacheState]) => {
      const shouldInclude =
        cacheKey.startsWith(loginStatePrefix) && cacheState.suggestedFollowers;

      if (shouldInclude) {
        allSuggestedUsers.push(...cacheState.suggestedFollowers);
      }
    },
  );

  const seenIds = new Set<string>();
  return allSuggestedUsers.filter((user) => {
    if (seenIds.has(user._id)) return false;
    seenIds.add(user._id);
    return true;
  });
};

export const UserSuggestionsForSearchSelector = selector<ISearchItem[]>({
  key: "UserSuggestionsForSearchSelector_v1",
  get: ({ get }) => {
    const userSuggestionsForSearchCacheState = get(
      UserSuggestionsForSearchState,
    );
    const allstarUser = get(AllstarUser);
    const isLoggedIn = !!allstarUser?.user?._id;

    const uniqueUserSuggestionsForSearch = getUserSuggestionsForSearch(
      userSuggestionsForSearchCacheState,
      isLoggedIn,
    );

    const suggestedUsers = filterAndMapUsers(
      uniqueUserSuggestionsForSearch,
      20,
    );

    return suggestedUsers;
  },
});
